'use strict';

/**
 * @fileoverview `@puffin-core/retry-after` — the ONE reader of an HTTP `Retry-After` header, and the ONE writer of the
 * one we send (CPAAS-RETRY-AFTER-001).
 *
 * Ten readers had been written by hand, and each read the header differently: a fraction read as whole seconds, a
 * negative read as a negative wait, `'1.5'` or an ISO stamp read as a date in 2001, an asctime date read in the
 * server's local zone, a WHATWG `Headers` read by bracket (always `undefined`, so the carriers' 429 waits were never
 * seen at all). The grammar is RFC 9110's, read strictly:
 *
 * - §10.2.3: `Retry-After = HTTP-date / delay-seconds`, `delay-seconds = 1*DIGIT`. Anything else is not a wait, and
 *   answers `null` — the caller falls back to its own backoff, never to a wait nobody asked for.
 * - §5.6.7: a recipient MUST accept all three HTTP-date formats — IMF-fixdate, RFC 850 and asctime — and every one of
 *   them is GMT. An RFC 850 two-digit year that appears more than 50 years in the future is the most recent past year
 *   with those digits. A date at or before now is a wait of 0, never a negative wait.
 *
 * There is NO cap here. How long a caller is willing to wait is the caller's own policy (a carrier send in the request
 * path, a background sync, a browser resolve): each states its own ceiling over the number this answers.
 *
 * @module @puffin-core/retry-after
 */

/** The header's name, as every carrier reads it (lower-case: `Headers.get` and plain objects compare against it). */
const RETRY_AFTER_HEADER = 'retry-after';

const SECOND_MS = 1000;

/** `1*DIGIT` (RFC 9110 §10.2.3). */
const DELAY_SECONDS = /^\d+$/;

const MONTHS = Object.freeze([
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]);
const MONTH_ALTERNATION = MONTHS.join('|');
const SHORT_DAYS = 'Mon|Tue|Wed|Thu|Fri|Sat|Sun';
const LONG_DAYS = 'Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday';
const TIME = '(\\d{2}):(\\d{2}):(\\d{2})';

/** `Sun, 06 Nov 1994 08:49:37 GMT` */
const IMF_FIXDATE = new RegExp(
  `^(?:${SHORT_DAYS}), (\\d{2}) (${MONTH_ALTERNATION}) (\\d{4}) ${TIME} GMT$`,
);
/** `Sunday, 06-Nov-94 08:49:37 GMT` */
const RFC_850_DATE = new RegExp(
  `^(?:${LONG_DAYS}), (\\d{2})-(${MONTH_ALTERNATION})-(\\d{2}) ${TIME} GMT$`,
);
/** `Sun Nov  6 08:49:37 1994` — a one-digit day is padded with a space. */
const ASCTIME_DATE = new RegExp(
  `^(?:${SHORT_DAYS}) (${MONTH_ALTERNATION}) ([ \\d]\\d) ${TIME} (\\d{4})$`,
);

/** RFC 9110 §5.6.7: an RFC 850 year more than this many years ahead belongs to the previous century. */
const TWO_DIGIT_YEAR_HORIZON_YEARS = 50;

/**
 * @param {*} now
 * @returns {() => number} the clock
 * @throws {TypeError} unless it is a function
 */
function requireClock(now) {
  if (typeof now !== 'function') {
    throw new TypeError('retry-after: `now` must be a function answering epoch milliseconds');
  }
  return now;
}

/**
 * @param {() => number} now
 * @returns {number} the clock's reading
 * @throws {TypeError} when it is not a finite number
 */
function readClock(now) {
  const at = now();
  if (!Number.isFinite(at)) {
    throw new TypeError('retry-after: `now()` must answer a finite number of epoch milliseconds');
  }
  return at;
}

/**
 * The instant named by date parts, or null when they name no real instant: RFC 9110 §5.6.7's ranges — a day that
 * exists in its month (no 31 Nov, a 29 Feb only in a leap year), hour 00–23, minute 00–59, second 00–59 (no leap
 * second).
 *
 * @param {{year: number, monthName: string, day: string, hour: string, minute: string, second: string}} parts
 * @returns {?number} epoch milliseconds
 */
function instantOf({ year, monthName, day, hour, minute, second }) {
  const month = MONTHS.indexOf(monthName);
  const [d, h, m, s] = [day, hour, minute, second].map(Number);
  const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  if (d < 1 || d > daysInMonth || h > 23 || m > 59 || s > 59) return null;
  return Date.UTC(year, month, d, h, m, s);
}

/**
 * RFC 9110 §5.6.7: the two digits are read in the current century, and a year that then appears more than 50 years in
 * the future is the most recent past year with those digits.
 *
 * @param {number} twoDigitYear
 * @param {Omit<Parameters<typeof instantOf>[0], 'year'>} parts
 * @param {number} nowMs
 * @returns {?number} epoch milliseconds
 */
function rfc850InstantOf(twoDigitYear, parts, nowMs) {
  const nowDate = new Date(nowMs);
  const horizon = Date.UTC(
    nowDate.getUTCFullYear() + TWO_DIGIT_YEAR_HORIZON_YEARS,
    nowDate.getUTCMonth(),
    nowDate.getUTCDate(),
    nowDate.getUTCHours(),
    nowDate.getUTCMinutes(),
    nowDate.getUTCSeconds(),
  );
  const year = Math.floor(nowDate.getUTCFullYear() / 100) * 100 + twoDigitYear;
  const at = instantOf({ ...parts, year });
  if (at === null || at <= horizon) return at;
  return instantOf({ ...parts, year: year - 100 });
}

/**
 * The instant an HTTP-date names, in any of its three formats, or null when the value is none of them.
 *
 * @param {string} value
 * @param {() => number} now
 * @returns {?{at: number, nowMs: number}}
 */
function httpDateOf(value, now) {
  const imf = IMF_FIXDATE.exec(value);
  if (imf) {
    const [, day, monthName, year, hour, minute, second] = imf;
    const at = instantOf({ year: Number(year), monthName, day, hour, minute, second });
    return at === null ? null : { at, nowMs: readClock(now) };
  }
  const rfc850 = RFC_850_DATE.exec(value);
  if (rfc850) {
    const [, day, monthName, year, hour, minute, second] = rfc850;
    const nowMs = readClock(now);
    const at = rfc850InstantOf(Number(year), { monthName, day, hour, minute, second }, nowMs);
    return at === null ? null : { at, nowMs };
  }
  const asctime = ASCTIME_DATE.exec(value);
  if (asctime) {
    const [, monthName, day, hour, minute, second, year] = asctime;
    const at = instantOf({ year: Number(year), monthName, day, hour, minute, second });
    return at === null ? null : { at, nowMs: readClock(now) };
  }
  return null;
}

/**
 * The wait one `Retry-After` field value asks for, in milliseconds.
 *
 * @param {*} value - the field value: a string, or a raw header list holding exactly one
 * @param {{now?: () => number}} [options] - the clock a date is measured from (epoch ms); read only for a date
 * @returns {?number} a non-negative integer number of milliseconds (a delay too large to represent reads as
 *   `Number.MAX_SAFE_INTEGER`, which every ceiling refuses), or null when the value is not a Retry-After
 * @throws {TypeError} when `now` is not a function, or answers no finite number
 */
function retryAfterMsFromValue(value, { now = Date.now } = {}) {
  const clock = requireClock(now);
  const single = Array.isArray(value) ? (value.length === 1 ? value[0] : null) : value;
  if (typeof single !== 'string') return null;
  const field = single.trim();
  if (DELAY_SECONDS.test(field)) {
    const ms = Number(field) * SECOND_MS;
    return Number.isSafeInteger(ms) ? ms : Number.MAX_SAFE_INTEGER;
  }
  const date = httpDateOf(field, clock);
  if (date === null) return null;
  return Math.max(0, date.at - date.nowMs);
}

/**
 * The raw `Retry-After` value a header carrier holds, or undefined. Reads every carrier the providers and clients hand
 * us: a WHATWG `Headers` or axios `AxiosHeaders` (anything with `get`), a `Map`, or a plain object — the last two by a
 * case-insensitive key, refusing one that holds the header under two spellings.
 *
 * @param {*} headers
 * @returns {*} the value, or undefined
 */
function rawValueOf(headers) {
  if (headers === null || typeof headers !== 'object') return undefined;
  if (headers instanceof Map) return valueByKey([...headers.entries()]);
  if (typeof headers.get === 'function') return headers.get(RETRY_AFTER_HEADER) ?? undefined;
  return valueByKey(Object.entries(headers));
}

/**
 * @param {Array<[*, *]>} entries
 * @returns {*} the one entry's value whose key is `retry-after` in any case; undefined for none or for two
 */
function valueByKey(entries) {
  const matches = entries.filter(
    ([key]) => typeof key === 'string' && key.toLowerCase() === RETRY_AFTER_HEADER,
  );
  return matches.length === 1 ? matches[0][1] : undefined;
}

/**
 * The wait a response's `Retry-After` header asks for, in milliseconds.
 *
 * @param {*} headers - the response's headers: a WHATWG `Headers`, an `AxiosHeaders`, a `Map` or a plain object
 * @param {{now?: () => number}} [options] - the clock a date is measured from (epoch ms)
 * @returns {?number} non-negative integer milliseconds, or null when there is no readable Retry-After
 * @throws {TypeError} when `now` is not a function, or answers no finite number
 */
function retryAfterMsOf(headers, options) {
  return retryAfterMsFromValue(rawValueOf(headers), options);
}

/**
 * The `Retry-After` value we send for a wait: whole delay-seconds, rounded UP, so a client never returns before the
 * wait it was told.
 *
 * @param {number} ms - a non-negative, finite number of milliseconds
 * @returns {string}
 * @throws {TypeError} for anything else — a wait the caller has not computed is a programming error
 */
function retryAfterHeaderValue(ms) {
  if (!Number.isFinite(ms) || ms < 0) {
    throw new TypeError(
      'retry-after: a wait to send must be a non-negative, finite number of milliseconds',
    );
  }
  return String(Math.ceil(ms / SECOND_MS));
}

module.exports = {
  RETRY_AFTER_HEADER,
  retryAfterHeaderValue,
  retryAfterMsFromValue,
  retryAfterMsOf,
};
